import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, MapPin, Phone, Mail, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  { icon: MapPin, label: "Address", value: "23, Moniya Road, Ojoo Ibadan, Oyo State, Nigeria, 200213" },
  { icon: Phone, label: "Phone", value: "+234 815 611 1156" },
  { icon: Mail, label: "Email", value: "info@ictsummit.org" },
  { icon: Globe, label: "Website", value: "ictsummit.org" },
];

const locations = ["Ibadan", "Lagos", "Ilorin", "Abuja", "Saki"];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [newsletter, setNewsletter] = useState("");
  const [nlLoading, setNlLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletter) {
      toast.error("Please enter your email");
      return;
    }
    setNlLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletter });
      toast.success("Successfully subscribed to our newsletter!");
      setNewsletter("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Subscription failed.";
      toast.error(msg);
    } finally {
      setNlLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-zinc-900/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Info */}
          <div>
            <span className="section-label">Contact Us</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white mt-4 mb-6">
              Get in <span className="gradient-text">touch</span>
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-10">
              Have questions about the summit, partnership opportunities, or speaking engagements?
              We'd love to hear from you.
            </p>

            <div className="space-y-6 mb-10">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-4" data-testid={`contact-info-${item.label.toLowerCase()}`}>
                  <div className="w-10 h-10 bg-teal-600/10 flex items-center justify-center flex-shrink-0">
                    <item.icon size={18} className="text-teal-500" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{item.label}</div>
                    <div className="text-zinc-300 text-sm mt-1">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Locations */}
            <div className="mb-10">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-3">Our Presence</div>
              <div className="flex flex-wrap gap-2">
                {locations.map((loc) => (
                  <span
                    key={loc}
                    className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs font-medium tracking-wide"
                  >
                    {loc}
                  </span>
                ))}
              </div>
            </div>

            {/* Newsletter */}
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="text-white font-bold tracking-tight mb-2">Stay Updated</h3>
              <p className="text-zinc-400 text-sm mb-4">Subscribe to our newsletter for summit updates and tech insights.</p>
              <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
                <input
                  type="email"
                  value={newsletter}
                  onChange={(e) => setNewsletter(e.target.value)}
                  className="flex-1 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="your@email.com"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={nlLoading}
                  className="bg-teal-600 hover:bg-teal-500 text-white px-5 py-3 font-semibold transition-all disabled:opacity-50"
                  data-testid="newsletter-submit-btn"
                >
                  {nlLoading ? <Loader2 size={18} className="animate-spin" /> : "Subscribe"}
                </button>
              </form>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              className="bg-zinc-900 border border-zinc-800 p-8"
              data-testid="contact-form"
            >
              <h3 className="text-xl font-bold text-white tracking-tight mb-6">Send a Message</h3>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="+234 XXX XXX XXXX"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="What's this about?"
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                    placeholder="Your message..."
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white py-4 font-semibold tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
